import { http } from '@/app/lib/http/client'

export type PromotionType = 'percentage' | 'fixed_amount' | 'buy_x_pay_y'
export type PromotionChannel = 'pos' | 'ecommerce' | 'both'
export type PromotionScope = 'global' | 'branches'
export type PromotionLifecycle = 'scheduled' | 'active' | 'expired' | 'inactive'
export type FixedApplication = 'per_unit' | 'per_line'

export interface PromotionOption {
  id: number
  name: string
  sku?: string | null
  barcode?: string | null
  is_active?: boolean
}

export interface Promotion {
  id: number
  company_id: number
  name: string
  description?: string | null
  type: PromotionType
  channel: PromotionChannel
  scope_type: PromotionScope
  value: string | null
  fixed_application: FixedApplication | null
  buy_quantity: number | null
  pay_quantity: number | null
  starts_at: string
  ends_at: string
  is_active: boolean
  lifecycle_status: PromotionLifecycle
  products: PromotionOption[]
  excluded_products: PromotionOption[]
  categories: PromotionOption[]
  departments: PromotionOption[]
  sucursals: PromotionOption[]
}

export interface PromotionListItem
  extends Omit<
    Promotion,
    | 'products'
    | 'excluded_products'
    | 'categories'
    | 'departments'
    | 'sucursals'
  > {
  products_count: number
  excluded_products_count: number
  categories_count: number
  departments_count: number
  sucursals_count: number
  applicable_products_count: number
}

export interface PromotionTargetProduct extends PromotionOption {
  category?: PromotionOption | null
  department?: PromotionOption | null
  matched_by: Array<'product' | 'category' | 'department'>
}

export interface PromotionPayload {
  name: string
  description: string | null
  type: PromotionType
  channel: PromotionChannel
  scope_type: PromotionScope
  value: string | null
  fixed_application: FixedApplication | null
  buy_quantity: number | null
  pay_quantity: number | null
  starts_at: string
  ends_at: string
  is_active: boolean
  product_ids: number[]
  excluded_product_ids: number[]
  category_ids: number[]
  department_ids: number[]
  sucursal_ids: number[]
}

export interface PromotionFilters {
  search?: string
  type?: PromotionType
  channel?: PromotionChannel
  scope_type?: PromotionScope
  lifecycle_status?: PromotionLifecycle
  product_id?: number
  sucursal_id?: number
  page?: number
  per_page?: number
}

export interface PromotionConflict {
  strategy: 'promotion' | 'quantity_tier'
  promotion_id?: number
  quantity_tier_id?: number
  name?: string
  product_ids: number[]
  sucursal_ids: Array<number | '*'>
  channels: Array<'pos' | 'ecommerce'>
  starts_at: string
  ends_at: string
}

export interface LaravelPaginator<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
}

export interface PromotionApiError {
  message?: string
  errors?: Record<string, string[] | string | PromotionConflict[]>
}

function paginator<T>(value: LaravelPaginator<T> | undefined): LaravelPaginator<T> {
  return value ?? {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: null,
    to: null,
  }
}

type OptionParams = { search?: string; page?: number; per_page?: number }

export const promotionService = {
  async list(
    filters: PromotionFilters,
  ): Promise<LaravelPaginator<PromotionListItem>> {
    const response = await http.get('/promotions', { params: filters })
    return paginator(response.data?.data?.promotions)
  },

  async get(id: number): Promise<Promotion> {
    const response = await http.get(`/promotions/${id}`)
    return response.data.data.promotion
  },

  async create(payload: PromotionPayload): Promise<Promotion> {
    const response = await http.post('/promotions', payload)
    return response.data.data.promotion
  },

  async update(id: number, payload: PromotionPayload): Promise<Promotion> {
    const response = await http.put(`/promotions/${id}`, payload)
    return response.data.data.promotion
  },

  async updateStatus(id: number, isActive: boolean): Promise<Promotion> {
    const response = await http.patch(`/promotions/${id}/status`, {
      is_active: isActive,
    })
    return response.data.data.promotion
  },

  async remove(id: number): Promise<void> {
    await http.delete(`/promotions/${id}`)
  },

  async productOptions(params: OptionParams = {}) {
    const response = await http.get('/promotions/options/products', { params })
    return paginator<PromotionOption>(response.data?.data?.products)
  },

  async sucursalOptions(params: OptionParams = {}) {
    const response = await http.get('/promotions/options/sucursals', { params })
    return paginator<PromotionOption>(response.data?.data?.sucursals)
  },

  async categoryOptions(params: OptionParams = {}) {
    const response = await http.get('/promotions/options/categories', { params })
    return paginator<PromotionOption>(response.data?.data?.categories)
  },

  async departmentOptions(params: OptionParams = {}) {
    const response = await http.get('/promotions/options/departments', {
      params,
    })
    return paginator<PromotionOption>(response.data?.data?.departments)
  },

  async applicableProducts(id: number, params: OptionParams = {}) {
    const response = await http.get(`/promotions/${id}/products`, { params })
    return paginator<PromotionTargetProduct>(response.data?.data?.products)
  },
}
